import {
  Children,
  ComponentType,
  createContext,
  isValidElement,
  ReactNode,
  useContext,
} from 'react';

/**
 * Distributes a flat `children` list into fixed positions inside the chart.
 *
 * `LineChart` cannot let the caller place its children directly: axes have to
 * be emitted inside the `<svg>` among the sibling `<Group>`s, and tooltips have
 * to be emitted inside `TooltipPortal` and `TooltipContext`, which only exist
 * while a tooltip is open. So the caller writes a flat list of sub-components
 * and the chart decides where each one lands.
 *
 * This is deliberately private to `LineChart`. Do not lift it back out into a
 * shared primitive: children are re-rendered once per slot, misplaced children
 * disappear silently, and slot names are unchecked strings. That cost only
 * buys something here, where the render position carries SVG structure and
 * context. Anywhere the parent is merely nesting children to satisfy its own
 * stylesheet, use plain compound components and let the caller own the tree.
 */
const SlotContext = createContext<string | undefined>(undefined);

const SLOT_NAME = Symbol('LineChartSlotName');

interface SlotComponent {
  [SLOT_NAME]?: string;
}

/** Renders only the children bound to `name`, in the order the chart wants. */
export function ChartSlot({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  return (
    <SlotContext.Provider value={name}>
      {Children.map(children, (child) =>
        isValidElement(child) &&
        (child.type as SlotComponent)[SLOT_NAME] === name
          ? child
          : null
      )}
    </SlotContext.Provider>
  );
}

/**
 * Binds a sub-component to a slot, so it renders where the chart places that
 * slot and nowhere else.
 */
export function asChartSlot<P extends object>(
  name: string,
  Component: ComponentType<P>
) {
  function SlotBoundComponent(props: P) {
    // Rendered outside its slot — the chart has already placed the real one.
    if (useContext(SlotContext) !== name) return null;

    return <Component {...props} />;
  }

  SlotBoundComponent.displayName = `ChartSlot(${Component.displayName || Component.name || 'Component'})`;
  SlotBoundComponent[SLOT_NAME] = name;

  return SlotBoundComponent;
}
